import type { StartTrainingBody } from "./mutations";
import type { Family } from "./queries";

export type TabularTaskType = "classification" | "regression" | "quantile";

export interface TrainFormState {
  datasetId: string;
  family: Family | null;
  tabularTaskType: TabularTaskType | null;
  label: string;
  modelName: string;
  mode: string;
  evalMetric: string;
  timeLimitMinutes: string;
  forecast: {
    timeCol: string;
    groupCol: string;
    predictionLength: string;
    frequency: string;
  };
}

export type ValidationErrors = Partial<
  Record<
    | "datasetId"
    | "family"
    | "tabularTaskType"
    | "label"
    | "modelName"
    | "mode"
    | "timeCol"
    | "predictionLength",
    string
  >
>;

export function validateFormState(s: TrainFormState): ValidationErrors {
  const errs: ValidationErrors = {};
  if (!s.datasetId) errs.datasetId = "Pick a dataset";
  if (!s.family) errs.family = "Pick a family";
  if (!s.modelName.trim()) errs.modelName = "Model name is required";
  if (!s.mode) errs.mode = "Pick a mode";
  if (s.family === "tabular") {
    if (!s.tabularTaskType) errs.tabularTaskType = "Pick a task type";
    if (!s.label.trim()) errs.label = "Label column is required";
  }
  if (s.family === "forecast") {
    if (!s.label.trim()) errs.label = "Value column is required";
    if (!s.forecast.timeCol.trim()) errs.timeCol = "Time column is required";
    const n = Number(s.forecast.predictionLength);
    if (!s.forecast.predictionLength || !Number.isInteger(n) || n < 1) {
      errs.predictionLength = "Prediction length must be a positive integer";
    }
  }
  return errs;
}

export function buildTrainingPayload(s: TrainFormState, projectId: string): StartTrainingBody {
  const cfg: Record<string, unknown> = { family: s.family, mode: s.mode };
  let task_type = "";
  let labelOut: string | null = null;

  if (s.family === "tabular" && s.tabularTaskType) {
    task_type = `tabular_${s.tabularTaskType}`;
    cfg.task_type = task_type;
    cfg.label = s.label.trim();
    labelOut = s.label.trim();
  } else if (s.family === "anomaly") {
    task_type = "anomaly_detection";
    cfg.task_type = task_type;
  } else if (s.family === "forecast") {
    task_type = "timeseries_forecast";
    cfg.task_type = task_type;
    cfg.value_col = s.label.trim();
    cfg.time_col = s.forecast.timeCol.trim();
    cfg.prediction_length = Number(s.forecast.predictionLength);
    if (s.forecast.groupCol.trim()) cfg.group_col = s.forecast.groupCol.trim();
    if (s.forecast.frequency.trim()) cfg.freq = s.forecast.frequency.trim();
    labelOut = null;
  }

  if (s.evalMetric.trim()) cfg.eval_metric = s.evalMetric.trim();
  if (s.timeLimitMinutes.trim()) {
    const m = Number(s.timeLimitMinutes);
    if (Number.isFinite(m) && m > 0) cfg.time_limit = Math.round(m * 60);
  }

  return {
    project_id: projectId,
    dataset_id: s.datasetId,
    model_name: s.modelName.trim(),
    task_type,
    label: labelOut,
    training_config: cfg,
  };
}
